import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface WidgetCardProps {
  title: string;
  /** Иконка в цветной плитке слева от заголовка */
  icon?: React.ReactNode;
  /** Цвет плитки иконки (акцент виджета) */
  accent?: string;
  /** Короткая подпись справа от заголовка (например, количество записей) */
  caption?: string;
  /** Произвольный элемент в правой части шапки (итог, счетчик и т.п.) */
  headerExtra?: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  /** Фильтры/действия под заголовком */
  toolbar?: React.ReactNode;
  /** Тело карточки прокручивается внутри, когда записей много (по умолчанию да) */
  scroll?: boolean;
  children: React.ReactNode;
}

/** Единая карточка виджета главной: светлая, плоская; высоту задает контейнер, избыток содержимого скроллится внутри */
const WidgetCard: React.FC<WidgetCardProps> = ({
  title, icon, accent = '#b91c1c', caption, headerExtra, loading, error, onRefresh, toolbar, scroll = true, children
}) => (
  <Box
    sx={{
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      minWidth: 0,
      minHeight: 0,
      overflow: 'hidden'
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, flexShrink: 0 }}>
      {icon && (
        <Box
          sx={{
            width: 32, height: 32, borderRadius: '9px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: accent, bgcolor: `${accent}14`,
            '& svg': { fontSize: 19 }
          }}
        >
          {icon}
        </Box>
      )}
      <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#0f172a', whiteSpace: 'nowrap' }}>{title}</Typography>
      {caption && <Typography sx={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{caption}</Typography>}
      <Box sx={{ flex: 1 }} />
      {headerExtra}
      {loading && <CircularProgress size={16} thickness={5} sx={{ color: accent }} />}
      {onRefresh && (
        <Tooltip title="Обновить">
          <span>
            <IconButton size="small" onClick={onRefresh} disabled={loading} sx={{ color: '#94a3b8' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
    {toolbar && <Box sx={{ px: 2, pb: 1.25, flexShrink: 0 }}>{toolbar}</Box>}
    <Box sx={{ borderTop: '1px solid #f1f5f9', flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      {error ? (
        <Typography sx={{ p: 2, fontSize: 13, color: '#dc2626' }}>{error}</Typography>
      ) : (
        <Box
          sx={{
            flex: 1, minHeight: 0, minWidth: 0,
            overflow: scroll ? 'auto' : 'hidden',
            display: scroll ? 'block' : 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': { width: 8, height: 8 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 8 }
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  </Box>
);

export default WidgetCard;
